import { 
  collection, 
  doc, 
  getDoc,
  getDocs, 
  query, 
  where, 
  writeBatch 
} from 'firebase/firestore';
import { toast } from 'sonner';
import { db } from '../../lib/firebase';
import { LogFunction, COLLECTIONS } from './types';
import { isUserAdmin } from './helpers';
import { 
  setSystemStatus, 
} from '../../lib/api';
import type { User as FirestoreUser } from '../../types/firestore';

/**
 * Get all users with admin privileges
 */
export const getAdminUsers = async (): Promise<Record<string, FirestoreUser>> => {
  const adminUsers: Record<string, FirestoreUser> = {};
  
  try {
    const usersQuery = query(collection(db, 'users'), where('isAdmin', '==', true));
    const querySnapshot = await getDocs(usersQuery);
    
    querySnapshot.forEach((userDoc) => {
      adminUsers[userDoc.id] = userDoc.data() as FirestoreUser;
    });
    
    return adminUsers;
  } catch (error) {
    console.error('Error fetching admin users:', error);
    return {};
  }
};

/**
 * Clears all data from Firestore collections except admin users and their usernames
 * @param handleTriggers - Whether to disable/enable triggers (set to false when called from importFirestoreData)
 */
export const clearFirestoreData = async (logFn?: LogFunction, handleTriggers: boolean = true): Promise<void> => {
  // Check admin status before proceeding
  const isAdmin = await isUserAdmin();
  if (!isAdmin) {
    logFn?.('Permission denied: Admin access required');
    throw new Error('Permission denied: Admin access required');
  }
  
  try {
    logFn?.('Starting database clearing operation');
    
    // Disable Algolia sync if handleTriggers is true
    if (handleTriggers) {
      logFn?.('Disabling Algolia sync');
      try {
        // Pass the payload directly, no more `as any` needed due to updated setSystemStatus signature
        await setSystemStatus({
          algoliaStatus: 'disabled'
        });
        logFn?.('Successfully disabled Algolia sync');
      } catch (error) {
        // If disable operation fails, abort the entire clear process
        const errorMessage = error instanceof Error ? error.message : String(error);
        logFn?.(`CRITICAL ERROR: Failed to disable Algolia sync: ${errorMessage}`);
        toast.error(`Operation aborted: Failed to disable Algolia sync properly. This prevents potential data corruption.`);
        
        throw new Error(`Operation aborted: Failed to safely disable Algolia sync: ${errorMessage}`);
      }
    }
    
    // Save admin users before clearing
    logFn?.('Fetching admin users to preserve');
    const adminUsers = await getAdminUsers();
    const adminUserIds = Object.keys(adminUsers);
    logFn?.(`Found ${adminUserIds.length} admin users to preserve`);

    // Fetch admin usernames to preserve
    logFn?.('Fetching admin usernames to preserve');
    const adminUsernames: Record<string, { userId: string }> = {};
    for (const userId of adminUserIds) {
      const userDoc = adminUsers[userId];
      if (userDoc.username) {
        const usernameDocRef = doc(db, 'userNames', userDoc.username.toLowerCase());
        const usernameDocSnap = await getDoc(usernameDocRef);
        if (usernameDocSnap.exists()) {
          adminUsernames[usernameDocSnap.id] = usernameDocSnap.data() as { userId: string };
          logFn?.(`Found username to preserve: ${usernameDocSnap.id}`);
        } else {
           logFn?.(`Warning: Username document not found for admin ${userId} with username ${userDoc.username}`);
        }
      } else {
        logFn?.(`Warning: Admin user ${userId} does not have a username field.`);
      }
    }
    logFn?.(`Found ${Object.keys(adminUsernames).length} admin usernames to preserve`);

    // Clear collections, skipping 'users' entirely and preserving admin 'userNames'
    for (const collectionName of COLLECTIONS) {
      // Skip the 'users' collection entirely in this loop
      if (collectionName === 'users') {
        logFn?.(`Skipping 'users' collection in initial clear phase.`);
        continue;
      }
      
      logFn?.(`Processing collection: ${collectionName}`);
      let batch = writeBatch(db);
      let batchCount = 0;
      const maxBatchSize = 500;
      
      const querySnapshot = await getDocs(collection(db, collectionName));
      const totalDocuments = querySnapshot.size;
      logFn?.(`Found ${totalDocuments} documents in ${collectionName}`);
      
      let deletedCount = 0;
      let preservedCount = 0; // Track preserved usernames
      
      const docs = querySnapshot.docs;
      
      // Process each document
      for (const document of docs) {
        // Special handling for 'usernames' collection
        if (collectionName === 'usernames') {
          // Preserve usernames belonging to admin users
          if (adminUsernames[document.id]) {
            logFn?.(`Preserving admin username: ${document.id}`);
            preservedCount++;
            continue; // Skip deletion
          }
        }

        batch.delete(doc(db, collectionName, document.id));
        batchCount++;
        deletedCount++;
        
        if (batchCount >= maxBatchSize) {
          logFn?.(`Committing batch delete of ${batchCount} documents from ${collectionName}`);
          await batch.commit();
          batch = writeBatch(db); // Create a new batch after committing
          batchCount = 0;
        }
      }
      
      if (batchCount > 0) {
        logFn?.(`Committing final batch delete of ${batchCount} documents from ${collectionName}`);
        await batch.commit();
      }
      
      logFn?.(`Deleted ${deletedCount} documents from ${collectionName}.`);
       if (collectionName === 'userNames' && preservedCount > 0) {
          logFn?.(`Preserved ${preservedCount} admin usernames.`);
       }
    }

    // Specifically clear non-admin users from the 'users' collection
    logFn?.('Clearing regular users from the \'users\' collection');
    const usersCollectionRef = collection(db, 'users');
    const usersSnapshot = await getDocs(usersCollectionRef);
    logFn?.(`Found ${usersSnapshot.size} total users to check`);
    
    let userBatch = writeBatch(db);
    let userBatchCount = 0;
    let deletedUserCount = 0;
    let preservedUserCount = 0; // Track preserved users

    const userDocs = usersSnapshot.docs;

    for (const userDoc of userDocs) {
        // Preserve admin users
        if (adminUsers[userDoc.id]) {
            logFn?.(`Preserving admin user: ${userDoc.id}`);
            preservedUserCount++;
            continue; // Skip deletion
        }

        // Delete non-admin user
        userBatch.delete(doc(db, 'users', userDoc.id));
        userBatchCount++;
        deletedUserCount++;

        if (userBatchCount >= 500) {
            logFn?.(`Committing batch delete of ${userBatchCount} regular users`);
            await userBatch.commit();
            userBatch = writeBatch(db); // Start a new batch
            userBatchCount = 0;
        }
    }

    // Commit any remaining deletions in the last batch
    if (userBatchCount > 0) {
        logFn?.(`Committing final batch delete of ${userBatchCount} regular users`);
        await userBatch.commit();
    }

    logFn?.(`Deleted ${deletedUserCount} regular users. Preserved ${preservedUserCount} admin users.`);

    // Keep Algolia sync disabled when handleTriggers is true
    if (handleTriggers) {
      logFn?.('Keeping Algolia sync disabled after operation - manual re-enabling required');
      try {
        // Ensure Algolia sync remains disabled - no more `as any`
        await setSystemStatus({
          algoliaStatus: 'disabled'
        });
        logFn?.('Keeping Algolia sync disabled');
        
        toast.info('Database cleared. Algolia sync is disabled. Please re-enable it manually when ready.');
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        logFn?.(`Warning: Failed to ensure Algolia sync remains disabled: ${errorMessage}`);
        toast.warning(`Database cleared, but failed to ensure Algolia sync remains disabled. Please verify system status.`);
      }
    }
    
    logFn?.('Database cleared successfully');
    toast.success("Database cleared successfully");
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error('Error clearing Firestore data:', error);
    logFn?.(`Error clearing data: ${errorMessage}`);
    
    // Ensure Algolia sync remains disabled after error when handleTriggers is true
    if (handleTriggers) {
      logFn?.('Keeping Algolia sync disabled after error - manual restart required');
      try {
        // No more `as any`
        await setSystemStatus({
          algoliaStatus: 'disabled'
        });
        logFn?.('Successfully kept Algolia sync disabled after error');
        toast.error('Critical error: Algolia sync has been disabled. Please manually re-enable when issues are resolved.');
      } catch (restoreError) {
        const restoreErrorMsg = restoreError instanceof Error ? restoreError.message : String(restoreError);
        logFn?.(`Warning: Failed to disable Algolia sync after error: ${restoreErrorMsg}`);
        toast.error(`Critical: Failed to disable Algolia sync. Please check system status.`);
      }
    }
    
    toast.error(`Failed to clear data: ${errorMessage}`);
    throw new Error(`Failed to clear data: ${errorMessage}`);
  }
}; 